/**
 * Trial extension offer — pure helpers.
 *
 * During a free trial, the billing owner can book a call with the team to get the
 * trial extended to `days` (14) in total from the trial start. These helpers decide
 * when the offer is available, build the Calendly booking URL, parse Calendly's
 * postMessage events and wrap browser storage. They have no React dependency so
 * they are trivial to unit test.
 *
 * Behavior parity with ordering-dashboard (feat/trial-extension-booking).
 */

export const TRIAL_EXTENSION_DAY_MS = 24 * 60 * 60 * 1000;
export const DEFAULT_TRIAL_EXTENSION_DAYS = 14;
export const DEFAULT_TRIAL_EXTENSION_BOOKING_URL =
  "https://calendly.com/orderingteam/ordering-demo";
export const CALENDLY_ORIGIN = "https://calendly.com";
export const CALENDLY_MIN_HEIGHT = 320;
export const CALENDLY_MAX_HEIGHT = 20000;
export const CALENDLY_LOAD_TIMEOUT_MS = 12000;
export const TRIAL_EXTENSION_BOOKED_PARAM = "trial_extension_booked";
/** DOM id of the top bar action; the banner hands focus to it when dismissed. */
export const TRIAL_EXTENSION_TOPBAR_ACTION_ID = "trial-extension-topbar-action";

const ENV_OFF_VALUES = ["false", "0", "off"];

/**
 * Build-time emergency switch VITE_TRIAL_EXTENSION_OFFER_ENABLED. The offer is on
 * by default (explicit exception to "new features default to hidden", requested
 * by product) and does not use PostHog: only "false", "0" or "off"
 * (case-insensitive) turn it off. Unset or any other value is on.
 */
export const isTrialExtensionEnvEnabled = (value) => {
  if (value === false || value === 0) return false;
  if (typeof value !== "string") return true;
  return !ENV_OFF_VALUES.includes(value.trim().toLowerCase());
};

/**
 * Offer configuration: build-time switch, booking URL and total trial days,
 * overridable with VITE_TRIAL_EXTENSION_OFFER_ENABLED,
 * VITE_TRIAL_EXTENSION_BOOKING_URL and VITE_TRIAL_EXTENSION_DAYS.
 */
export const getTrialExtensionConfig = (env = import.meta.env ?? {}) => {
  const days = Number(env.VITE_TRIAL_EXTENSION_DAYS);
  return {
    enabled: isTrialExtensionEnvEnabled(env.VITE_TRIAL_EXTENSION_OFFER_ENABLED),
    booking_url:
      env.VITE_TRIAL_EXTENSION_BOOKING_URL ||
      DEFAULT_TRIAL_EXTENSION_BOOKING_URL,
    days:
      Number.isFinite(days) && days > 0 ? days : DEFAULT_TRIAL_EXTENSION_DAYS,
  };
};

/**
 * Only the billing owner is offered the extension. Same definition as
 * useIsProjectOwner: level 0 + user_system_id (top-level account).
 */
export const isBillingOwner = (user) =>
  Boolean(user && user.level === 0 && user.user_system_id);

// "2026-10-03 12:00:00" or "2026-10-03T12:00:00(.000)" with no Z / ±hh:mm offset.
const NAIVE_DATE_TIME =
  /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)$/;

/**
 * Parses a date-ish value into epoch millis, or null. The billing API returns
 * trial dates without a timezone (`Y-m-d H:i:s` or naive ISO); they are UTC, so
 * naive strings are read as UTC instead of the browser's local time.
 */
export const parseDateValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  let input = value;
  if (typeof value === "string") {
    const match = value.trim().match(NAIVE_DATE_TIME);
    input = match ? `${match[1]}T${match[2]}Z` : value.trim();
  }
  const time = new Date(input).getTime();
  return Number.isFinite(time) ? time : null;
};

/** Whole days left until `trialEndTime`, floored at 0 and rounded up. */
export const getTrialDaysRemaining = (trialEndTime, now = Date.now()) => {
  if (!Number.isFinite(trialEndTime)) return 0;
  return Math.max(0, Math.ceil((trialEndTime - now) / TRIAL_EXTENSION_DAY_MS));
};

/** First subscription with status `trialing`, or null. */
export const findTrialSubscription = (subscriptions) =>
  Array.isArray(subscriptions)
    ? (subscriptions.find((s) => s?.status === "trialing") ?? null)
    : null;

/**
 * Eligibility for the offer. Detection is by subscription, not by the project's
 * current_status (core keeps the project "active" during a trial).
 *
 * available = enabled && booking_url && !white label build && billing owner &&
 *   trialing subscription && trial_end > now &&
 *   (trial_end - start_date) < (days - 0.5) days && !booked
 *
 * A missing start_date means unknown duration and does not block the offer.
 */
export const getTrialExtensionEligibility = ({
  config,
  user,
  trialSubscription,
  booked = false,
  now = Date.now(),
} = {}) => {
  const enabled =
    Boolean(config?.enabled) &&
    Boolean(config?.booking_url) &&
    !config?.is_white_label;
  const totalDays =
    Number(config?.days) > 0
      ? Number(config.days)
      : DEFAULT_TRIAL_EXTENSION_DAYS;

  const trialEnd = parseDateValue(trialSubscription?.trial_end);
  const startDate = parseDateValue(
    trialSubscription?.start_date ?? trialSubscription?.trial_start,
  );

  const isTrialing =
    Boolean(trialSubscription) &&
    trialSubscription.status === "trialing" &&
    trialEnd !== null;
  const trialActive = isTrialing && trialEnd > now;

  let underDurationCap = true;
  if (trialActive && startDate !== null) {
    underDurationCap =
      trialEnd - startDate < (totalDays - 0.5) * TRIAL_EXTENSION_DAY_MS;
  }

  const available = Boolean(
    enabled &&
    isBillingOwner(user) &&
    trialActive &&
    underDurationCap &&
    !booked,
  );

  return {
    available,
    days: totalDays,
    daysRemaining: trialActive ? getTrialDaysRemaining(trialEnd, now) : 0,
    trialEnd: isTrialing ? trialEnd : null,
  };
};

/**
 * Builds the Calendly booking URL with prefill, attribution and appearance params.
 * Pass `embedDomain` for the inline iframe variant (adds embed_domain/embed_type);
 * omit it for the "open in a new tab" link. Empty values are omitted.
 */
export const buildTrialExtensionBookingUrl = ({
  baseUrl,
  user,
  projectCode,
  source = "dashboard",
  embedDomain,
} = {}) => {
  if (!baseUrl) return "";

  let url;
  try {
    url = new URL(baseUrl);
  } catch {
    return "";
  }

  const name = `${user?.name || ""} ${user?.lastname || ""}`.trim();

  const entries = [
    ["name", name],
    ["email", user?.email],
    ["utm_source", source],
    ["utm_medium", "trial_extension"],
    ["utm_campaign", "free_trial"],
    ["utm_content", projectCode],
    ["hide_event_type_details", "1"],
    ["hide_gdpr_banner", "1"],
    ["embed_domain", embedDomain],
    ["embed_type", embedDomain ? "Inline" : ""],
  ];

  entries.forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    url.searchParams.set(key, String(value));
  });

  return url.toString();
};

/** Clamps a Calendly-reported height (e.g. "711px") to pixels, or null. */
export const clampCalendlyHeight = (rawHeight) => {
  const height = Number.parseFloat(String(rawHeight));
  if (!Number.isFinite(height)) return null;
  if (height < CALENDLY_MIN_HEIGHT || height > CALENDLY_MAX_HEIGHT) return null;
  return Math.ceil(height);
};

/**
 * Parses a `message` event from the Calendly inline embed. Returns null unless it
 * genuinely comes from the embedded Calendly iframe (origin + source window match).
 */
export const parseCalendlyMessage = (event, iframeWindow) => {
  if (!event || event.origin !== CALENDLY_ORIGIN) return null;
  if (!iframeWindow || event.source !== iframeWindow) return null;

  const type = event.data?.event;
  if (typeof type !== "string" || !type.startsWith("calendly.")) return null;

  const payload = event.data.payload;

  if (type === "calendly.page_height") {
    return { type, height: clampCalendlyHeight(payload?.height), payload };
  }

  return { type, payload };
};

// --- Storage (every access guarded: Safari private mode, quota, disabled storage) ---

export const trialExtensionStorageKeys = {
  booked: (projectKey) => `trial_extension_booked:${projectKey}`,
  popupShown: (projectKey) => `trial_extension_popup_shown:${projectKey}`,
  bannerDismissed: (projectKey) =>
    `trial_extension_banner_dismissed:${projectKey}`,
};

const getStorage = (kind) => {
  if (typeof window === "undefined") return null;
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

export const safeStorageGet = (kind, key) => {
  const storage = getStorage(kind);
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

export const safeStorageSet = (kind, key, value) => {
  const storage = getStorage(kind);
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    // The offer keeps working in memory; it just won't persist across reloads.
    return false;
  }
};

/**
 * ordering-accounts appends `trial_extension_booked=1` when the person already
 * booked the call before reaching the dashboard. Read once at boot.
 */
export const readBookedFromUrl = () => {
  if (typeof window === "undefined") return false;
  try {
    return (
      new URLSearchParams(window.location.search).get(
        TRIAL_EXTENSION_BOOKED_PARAM,
      ) === "1"
    );
  } catch {
    return false;
  }
};

/**
 * The project the boot URL refers to (`?project=`), captured together with
 * `trial_extension_booked` because AuthProvider strips `project` from the address
 * bar once it applies it. Null when the URL names no project.
 */
export const readProjectFromUrl = () => {
  if (typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get("project") || null;
  } catch {
    return null;
  }
};

/**
 * Removes `trial_extension_booked` from the address bar (keeping every other
 * param and the hash) so a reload or another project doesn't consume it again.
 */
export const removeBookedParamFromUrl = () => {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(TRIAL_EXTENSION_BOOKED_PARAM)) return;
    params.delete(TRIAL_EXTENSION_BOOKED_PARAM);
    const search = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`,
    );
  } catch {
    // Leaving the param in place is harmless: it is only read once at boot.
  }
};
